import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import CmsService from '@/services/CmsService';
import type { CmsSubmissionRow } from '@/services/CmsService';
import { freshTerminalRow, type CmsOp } from '@/logic/cmsResultCodes';
import { CMS_OPERATION } from '@/logic/cmsWindows';
import { CMS_KEYS } from './keys';

// CmsOp is the UI discriminator; the apiv2 `cms_submissions.operation` filter is
// exact-match, so it must receive the wire literal WF12 writes — 'check_eligibility'
// (NOT 'eligibility'). 'align' happens to coincide with its wire value.
const OP_TO_WIRE: Record<CmsOp, string> = {
  eligibility: CMS_OPERATION.eligibility,
  align: CMS_OPERATION.alignment,
  unalign: CMS_OPERATION.unalign,
  reporting: CMS_OPERATION.reporting,
};

// All-operations CMS submission read for the patient-drawer CMS card — feeds the
// reporting-window + in-flight math (logic/cmsWindows). Newest-first. Sits under
// ['cms'] so the existing CMS_KEYS.all invalidations (eligibility/align/reporting
// mutations) re-derive the card after a submit. Distinct key from the per-op poll.
export function useCmsSubmissions(withingsUserId?: string | null) {
  return useQuery<CmsSubmissionRow[]>({
    queryKey: CMS_KEYS.submissionsAll(String(withingsUserId || '')),
    queryFn: () => CmsService.getCmsSubmissions(String(withingsUserId)),
    enabled: !!withingsUserId,
    staleTime: 30_000,
  });
}

// CMS submission verdict poll (eligibility + align + unalign + reporting). The Tier-4 event
// acks at apiv2 (~5s SYNC_TIMEOUT) long before the CMS poll resolves (~12-16s), so
// the verdict arrives on the cms_submissions row, not the event response. While
// `enabled`, refetch every 5s until the FRESH row (one whose correlation_id was
// not in `baselineIds` at fire time — skew-immune identity, NOT a wall-clock
// guard) reaches a terminal verdict; then stop.
//
// The refetchInterval applies the SAME freshness + terminal predicate the modal's
// verdict effect uses (freshTerminalRow), so polling stops exactly when the modal
// resolves — never on a stale prior-attempt row.
const POLL_INTERVAL_MS = 5000;
export function useCmsSubmissionPoll(
  op: CmsOp,
  withingsUserId: string,
  { enabled, baselineIds }: { enabled: boolean; baselineIds: ReadonlySet<string> },
) {
  return useQuery<CmsSubmissionRow[]>({
    queryKey: CMS_KEYS.submissions(op, withingsUserId),
    queryFn: () => CmsService.getCmsSubmissions(withingsUserId, OP_TO_WIRE[op]),
    enabled: enabled && !!withingsUserId,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) => {
      const done = freshTerminalRow(query.state.data, baselineIds, op);
      return done ? false : POLL_INTERVAL_MS;
    },
  });
}

// Imperative one-shot: snapshot the correlation_ids of the submission rows that
// already exist for `op` BEFORE firing the event. The poll then latches onto the
// first row whose id is NOT in this set (skew-immune freshness — see
// firstFreshRow). Returns a stable callback so it can sit in a handler's deps.
export function useCaptureCmsBaseline(op: CmsOp) {
  return useCallback(async (withingsUserId: string): Promise<Set<string>> => {
    try {
      const rows = await CmsService.getCmsSubmissions(withingsUserId, OP_TO_WIRE[op]);
      return new Set((rows || []).map((r) => String(r.correlation_id)).filter(Boolean));
    } catch {
      // A failed baseline read must not block the fire — fall back to an empty
      // set (the poll then latches the newest row; acceptable since a fresh fire
      // almost always produces the newest row anyway).
      return new Set();
    }
  }, [op]);
}
